import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, ArrowLeft, Check, KeyRound, Loader2, AlertCircle } from 'lucide-react';
import { toast } from "sonner";
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PasswordResetRequest = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await axios.post(`${API_URL}/api/booking/auth/password-reset-request`, { email });
            setSent(true);
            toast.success('Falls ein Konto existiert, wurde eine E-Mail gesendet.');
        } catch (error) {
            toast.error('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <Navbar />
            
            <main className="flex-grow flex items-center justify-center py-12 px-4">
                <div className="w-full max-w-md">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/login')}
                        className="mb-6"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Zurück zum Login
                    </Button>

                    <Card>
                        <CardContent className="p-8">
                            {sent ? (
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Check className="h-8 w-8 text-green-600" />
                                    </div>
                                    <h2 className="text-2xl font-bold mb-2">E-Mail gesendet!</h2>
                                    <p className="text-gray-600 mb-6">
                                        Falls ein Konto mit <strong>{email}</strong> existiert, 
                                        haben wir Ihnen einen Link zum Zurücksetzen des Passworts gesendet.
                                    </p>
                                    <p className="text-sm text-gray-500 mb-6">
                                        Bitte prüfen Sie auch Ihren Spam-Ordner.
                                    </p>
                                    <Button onClick={() => navigate('/login')} className="w-full">
                                        Zurück zum Login
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <div className="text-center mb-6">
                                        <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                                            <KeyRound className="h-8 w-8 text-white" />
                                        </div>
                                        <h2 className="text-2xl font-bold">Passwort vergessen?</h2>
                                        <p className="text-gray-600 mt-2">
                                            Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen 
                                            einen Link zum Zurücksetzen Ihres Passworts.
                                        </p>
                                    </div>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">E-Mail-Adresse</label>
                                            <div className="relative">
                                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                <Input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="ihre@email.de"
                                                    className="pl-10"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <Button type="submit" className="w-full" disabled={loading}>
                                            {loading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Wird gesendet...
                                                </>
                                            ) : (
                                                'Reset-Link senden'
                                            )}
                                        </Button>
                                    </form>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </main>
            
            <Footer />
        </div>
    );
};

export const PasswordResetConfirm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (password.length < 8) {
            setError('Passwort muss mindestens 8 Zeichen lang sein.');
            return;
        }
        
        if (password !== confirmPassword) {
            setError('Die Passwörter stimmen nicht überein.');
            return;
        }
        
        setLoading(true);
        
        try {
            await axios.post(`${API_URL}/api/booking/auth/password-reset-confirm`, {
                token,
                new_password: password
            });
            setSuccess(true);
            toast.success('Passwort erfolgreich geändert!');
        } catch (err) {
            const message = err.response?.data?.detail || 'Ein Fehler ist aufgetreten.';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex flex-col bg-gray-50">
                <Navbar />
                <main className="flex-grow flex items-center justify-center py-12 px-4">
                    <Card className="max-w-md">
                        <CardContent className="p-8 text-center">
                            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                            <h2 className="text-xl font-bold mb-2">Ungültiger Link</h2>
                            <p className="text-gray-600 mb-4">
                                Der Reset-Link ist ungültig oder wurde bereits verwendet.
                            </p>
                            <Button onClick={() => navigate('/passwort-vergessen')}>
                                Neuen Link anfordern
                            </Button>
                        </CardContent>
                    </Card>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <Navbar />
            
            <main className="flex-grow flex items-center justify-center py-12 px-4">
                <div className="w-full max-w-md">
                    <Card>
                        <CardContent className="p-8">
                            {success ? (
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Check className="h-8 w-8 text-green-600" />
                                    </div>
                                    <h2 className="text-2xl font-bold mb-2">Passwort geändert!</h2>
                                    <p className="text-gray-600 mb-6">
                                        Ihr Passwort wurde erfolgreich geändert. 
                                        Sie können sich jetzt mit dem neuen Passwort anmelden.
                                    </p>
                                    <Button onClick={() => navigate('/login')} className="w-full">
                                        Zum Login
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <div className="text-center mb-6">
                                        <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                                            <KeyRound className="h-8 w-8 text-white" />
                                        </div>
                                        <h2 className="text-2xl font-bold">Neues Passwort</h2>
                                        <p className="text-gray-600 mt-2">
                                            Geben Sie Ihr neues Passwort ein.
                                        </p>
                                    </div>

                                    {error && (
                                        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                            {error}
                                        </div>
                                    )}

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Neues Passwort</label>
                                            <Input
                                                type="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder="Mindestens 8 Zeichen"
                                                required
                                                minLength={8}
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Passwort bestätigen</label>
                                            <Input
                                                type="password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                placeholder="Passwort wiederholen"
                                                required
                                            />
                                        </div>

                                        <Button type="submit" className="w-full" disabled={loading}>
                                            {loading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Wird gespeichert...
                                                </>
                                            ) : (
                                                'Passwort speichern'
                                            )}
                                        </Button>
                                    </form>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </main>
            
            <Footer />
        </div>
    );
};

export default PasswordResetRequest;
